/** Shapes returned by the tenancy admin operations (packages/tenancy/src/operations/admin). */

export type AiMode = 'off' | 'suggest' | 'auto';
export interface AiPolicy {
  default?: AiMode;
  capabilities: Record<string, AiMode>;
}

export interface OverviewSite {
  ref: string;
  name: string;
  type: string;
  market: string;
  hostname: string;
  basePath: string;
  status: string;
  role: string;
  canAdmin: boolean;
  canManagePeople: boolean;
}

export interface OverviewBrand {
  ref: string;
  name: string;
  slug: string;
  role: string | null;
  canAdmin: boolean;
  canManagePeople: boolean;
  sites: OverviewSite[];
}

export interface OverviewOrganization {
  ref: string;
  name: string;
  slug: string;
  status: string;
  isOperator: boolean;
  noAiContent: boolean;
  aiCeiling: AiPolicy | null;
  monthlyAiBudgetCents: number | null;
  role: string | null;
  canAdmin: boolean;
  canManagePeople: boolean;
  brands: OverviewBrand[];
}

export interface TenancyOverview {
  canCreateOrganization: boolean;
  organizations: OverviewOrganization[];
}

export interface SiteOptions {
  markets: { code: string; name: string; locale: string; currency: string }[];
  /** Languages with site texts; a locale's first part must be one of them. */
  languages: string[];
  types: string[];
  clickoutModes: string[];
  revealPolicies: string[];
  aiCapabilities: string[];
  aiModes: AiMode[];
  fontFamilies: string[];
  densities: string[];
}

export interface SiteSettings {
  siteName: string | null;
  descriptor: string | null;
  tagline: string | null;
  affiliateDisclosure: string | null;
  editorialOwner: string | null;
  searchConsoleProperty: string | null;
  thinMerchantMinLiveCoupons: number;
  publisherCategories: boolean;
  commercialDisclosure: boolean;
  showDemoNotice: boolean;
  headHtml: string | null;
  navigation: { items: NavItem[] } | null;
  consent: { provider: 'usercentrics'; rulesetId: string; idType: 'settings' | 'ruleset'; autoblocker: boolean } | null;
  legalNotice: { operator: string; address: string[]; representedBy?: string; register?: string; vatId?: string; email: string; phone?: string; contentResponsible?: string } | null;
  mascot: { name: string; enabled: boolean } | null;
  editorialTeam: { name: string; role: string }[];
}

/** One item of a site's main navigation (packages/tenancy/src/settings.ts navItemSchema). */
export type NavItem =
  | { type: 'home'; label?: string }
  | { type: 'stores'; label?: string }
  | { type: 'all_categories'; label?: string }
  | { type: 'shops'; label?: string; shops: string[]; limit?: number }
  | { type: 'categories'; label?: string; categories: string[]; limit?: number }
  | { type: 'campaigns'; label?: string; campaigns: string[] }
  | { type: 'page'; page: 'how_we_verify' | 'about' | 'affiliate_disclosure' | 'privacy' | 'legal'; label?: string }
  | { type: 'link'; label: string; href: string };

export interface SiteDetail {
  ref: string;
  name: string;
  type: string;
  status: string;
  organization: { ref: string; name: string; noAiContent: boolean };
  brand: { ref: string; name: string };
  market: string;
  defaultLocale: string;
  currency: string;
  timeZone: string | null;
  marketTimeZone: string;
  hostname: string;
  basePath: string;
  clickoutMode: string;
  revealPolicy: string;
  indexable: boolean;
  settings: SiteSettings;
  ai: { default: AiMode | null; capabilities: { capability: string; requested: AiMode | null; ceiling: AiMode; effective: AiMode }[] };
  domains: { id: number; hostname: string; basePath: string; isPrimary: boolean }[];
  routed: boolean;
  role: string;
  canAdmin: boolean;
}

export interface PlacementConfig {
  limit?: number;
  merchantSlugs?: string[];
  categorySlugs?: string[];
  layout?: 'list' | 'grid' | 'compact';
}

export interface Placement {
  ref: string;
  key: string;
  name: string;
  moduleType: string;
  clickoutMode: string | null;
  config: PlacementConfig;
  updatedAt: string;
}

export interface PlacementList {
  siteType: string;
  moduleTypes: string[];
  placements: Placement[];
}

export interface ApiKey {
  ref: string;
  name: string;
  prefix: string | null;
  allowedOrigins: string[];
  active: boolean;
  createdAt: string;
  createdBy: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export interface Membership {
  id: number;
  roles: string[];
  roleNames: string;
  capabilities: string[];
  level: 'organization' | 'brand' | 'site';
  organization: { ref: string; name: string };
  brand: { ref: string; name: string } | null;
  site: { ref: string; name: string } | null;
  canChange: boolean;
}

export interface StudioPerson {
  email: string;
  name: string;
  status: string;
  lastLoginAt: string | null;
  memberships: Membership[];
}

export interface AuditEntry {
  key: string;
  source: 'studio' | 'companion';
  occurredAt: string;
  actorKind: string;
  actorId: string;
  operation: string;
  outcome: string;
  via: string;
  input: unknown;
  result: unknown;
  error: string | null;
}

export type ThemeLevel = 'organization' | 'brand' | 'site';
export type ColorKey =
  | 'bg'
  | 'surface'
  | 'surfaceAlt'
  | 'border'
  | 'ink'
  | 'text'
  | 'muted'
  | 'primary'
  | 'onPrimary'
  | 'accent'
  | 'onAccent'
  | 'highlight'
  | 'success'
  | 'warning'
  | 'focus';

export interface ThemeDocument {
  color?: { light?: Partial<Record<ColorKey, string>>; dark?: Partial<Record<ColorKey, string>> };
  font?: { display?: { family: string }; text?: { family: string }; roles?: Partial<Record<'heading' | 'figure' | 'accent', 'display' | 'text'>> };
  radius?: Partial<Record<'sm' | 'md' | 'lg' | 'pill', string>>;
  density?: string;
  variants?: Record<string, string>;
}

export interface ContrastResult {
  scheme: 'light' | 'dark';
  text: string;
  background: string;
  textColor: string;
  backgroundColor: string;
  ratio: number;
  passes: boolean;
}

export interface Guardrails {
  ok: boolean;
  issues: { path: string; message: string }[];
  contrast: ContrastResult[];
  below: { level: ThemeLevel; ref: string; name: string; failing: ContrastResult[] }[];
}

export type Palette = Record<ColorKey, string>;

export interface ThemeLayer {
  level: ThemeLevel;
  ref: string;
  name: string;
  document: ThemeDocument;
  dropped: string[];
  inherited: ThemeDocument;
  resolved: ThemeDocument;
  palettes: { light: Palette; dark: Palette };
  guardrails: Guardrails;
  canEdit: boolean;
  menu: { fontFamilies: string[]; densities: string[]; variants: Record<string, string[]> };
}

export const TYPE_LABELS: Record<string, string> = {
  standalone_domain: 'Own domain',
  publisher_subfolder: 'Publisher subfolder',
  embed_only: 'Embed only',
  api_only: 'API only',
};

export const CLICKOUT_LABELS: Record<string, string> = {
  new_tab_modal: 'New tab + code modal',
  new_tab_merchant: 'Merchant in new tab',
  same_tab: 'Same tab',
};

export const REVEAL_LABELS: Record<string, string> = {
  single: 'One code per click',
  next_on_fail: 'Next code if one fails',
  all_after_click: 'All codes after a click',
};

export interface CapabilityInfo {
  key: string;
  area: string;
  label: string;
  description: string;
  levels: string[];
  operatorOnly: boolean;
}

export interface RoleInfo {
  key: string;
  name: string;
  description: string;
  capabilities: string[];
}

export interface CustomRoleInfo extends RoleInfo {
  ref: string;
  organization: { ref: string; name: string };
  members: number;
  canEdit: boolean;
}

export interface RolesCatalog {
  areas: { key: string; label: string }[];
  capabilities: CapabilityInfo[];
  templates: RoleInfo[];
  customRoles: CustomRoleInfo[];
  organizations: { ref: string; name: string; platformOperator: boolean; canManageRoles: boolean }[];
}
